import { useLocation, Navigate } from 'react-router-dom';

import { getSubdomainConfig } from '@/config/subdomain.config';
import { SUPPORTED_LANGUAGES, languageToUrlCode } from '@/utils/language';

// Component to redirect to default language based on subdomain or user preference
export const LanguageRedirect = () => {
  const location = useLocation();

  // Check if current path already has a valid language prefix
  const pathPart = location.pathname.split('/').find(Boolean);
  const firstPart = pathPart;
  if (firstPart && SUPPORTED_LANGUAGES.includes(firstPart)) {
    // URL already has a valid language prefix, don't redirect
    // This prevents infinite redirect loops
    return null;
  }

  // Determine target language synchronously to avoid flash
  const getTargetLanguage = () => {
    // Get subdomain config to determine default language
    const subdomainConfig = getSubdomainConfig() as { defaultLanguage: string; subdomain: string };
    const subdomainDefault = subdomainConfig.defaultLanguage;

    // For medipaths.co (main domain), ALWAYS default to English when visiting root
    // This ensures the main domain root (/) always redirects to /en regardless of user preferences
    // Note: Users can still access other languages via direct URLs (e.g., /ko/treatments)
    // and their language preferences will be respected when using the language switcher on language-prefixed pages
    if (subdomainConfig.subdomain === 'default') {
      const defaultLang = 'en';

      // Save preference for consistency (but don't set override flag)
      // This way, if user explicitly chooses a language later, their choice will be honored
      localStorage.setItem('preferred_language', defaultLang);

      return defaultLang;
    }

    // For other subdomains (e.g., vn.medipaths.co), respect user preferences
    const savedLanguage = localStorage.getItem('preferred_language');
    const userOverride = localStorage.getItem('language_user_override');

    // If user has explicitly chosen a language, use it
    if (savedLanguage && userOverride === 'true' && SUPPORTED_LANGUAGES.includes(savedLanguage)) {
      return savedLanguage;
    }

    // Otherwise use subdomain default
    const finalDefault = SUPPORTED_LANGUAGES.includes(subdomainDefault) ? subdomainDefault : 'en';

    // Save preference for future visits (only if no explicit user choice)
    if (!userOverride) {
      localStorage.setItem('preferred_language', finalDefault);
    }

    return finalDefault;
  };

  // Get target language and URL code
  const targetLang = getTargetLanguage();
  const urlCode = languageToUrlCode(targetLang);

  // Build target path preserving the current path
  // Remove leading slash if present to avoid double slashes
  const currentPath = location.pathname.startsWith('/')
    ? location.pathname.slice(1)
    : location.pathname;
  const targetPath = currentPath ? `/${urlCode}/${currentPath}` : `/${urlCode}`;

  // Preserve search parameters and hash
  const fullPath = `${targetPath}${location.search}${location.hash}`;

  // Use Navigate for immediate redirect (executes during render)
  return <Navigate to={fullPath} replace />;
};
